import { Button, type ButtonProps } from "design-system/atoms/Button"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { FancySparkles } from "design-system/atoms/icons/FancySparkles"
import { DropdownMenuItem } from "design-system/molecules/DropdownMenu"
import { useEnhanceMessageMutation } from "modules/messages/hooks/useEnhanceMessageMutation"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { CustomerResponseDataSchema } from "schema"
import { z } from "zod"

interface EnhanceMessageControlsProps {
  customer: z.infer<typeof CustomerResponseDataSchema>
  conversationId: string
  inputs: Record<string, string>
  onSetInputs: (callback: (prev: Record<string, string>) => Record<string, string>) => void
  mode?: "button" | "menuItem"
  buttonVariant?: ButtonProps["variant"]
  buttonSize?: ButtonProps["size"]
  showLabel?: boolean
}

export function EnhanceMessageControls({
  conversationId,
  inputs,
  onSetInputs,
  mode = "button",
  buttonVariant = "outline",
  buttonSize = "icon",
  showLabel = false,
}: EnhanceMessageControlsProps) {
  const { t } = useTranslation()
  const [enhancingMessages, setEnhancingMessages] = useState<Record<string, boolean>>({})

  const { mutateAsync: enhanceMessage } = useEnhanceMessageMutation({})

  const messageIsEmpty = !inputs[conversationId] || inputs[conversationId].length === 0
  const isEnhancing = enhancingMessages[conversationId] || false

  async function onClick() {
    if (messageIsEmpty) return

    setEnhancingMessages((prev) => ({ ...prev, [conversationId]: true }))

    try {
      const result = await enhanceMessage({
        conversationId: conversationId,
        content: inputs[conversationId] || "",
      })

      if (result?.data?.enhancedMessage) {
        onSetInputs((prev) => ({
          ...prev,
          [conversationId]: result.data?.enhancedMessage ?? "",
        }))
      }
    } finally {
      setEnhancingMessages((prev) => ({ ...prev, [conversationId]: false }))
    }
  }

  const label = t("conversations.chat.enhanceMessage")

  if (mode === "menuItem") {
    return (
      <DropdownMenuItem disabled={messageIsEmpty || isEnhancing} onSelect={onClick}>
        <FancySparkles size={16} strokeWidth={1} />
        <span>{label}</span>
      </DropdownMenuItem>
    )
  }

  const button = (
    <Button
      type="button"
      size={buttonSize}
      variant={buttonVariant}
      className="transition-opacity"
      aria-label={label}
      title={label}
      disabled={messageIsEmpty || isEnhancing}
      onClick={onClick}
    >
      <FancySparkles size={16} strokeWidth={1} />
      {showLabel && <span className="sr-only">{label}</span>}
    </Button>
  )

  if (showLabel) return button

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{button}</span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}
